import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FiEdit, FiPlus, FiBarChart2, FiCalendar, FiList } from 'react-icons/fi';
import articleService from '../../services/articleService';
import commentService from '../../services/commentService';

const DashboardContainer = styled.div`
  padding: 2rem 0;
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const DashboardTitle = styled.h1`
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
`;

const NewArticleButton = styled(Link)`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  font-family: ${props => props.theme.fonts.accent};
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:after {
    display: none;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${props => props.theme.colors.divider};
  margin-bottom: 2rem;
  overflow-x: auto;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding-bottom: 0.5rem;
  }
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 1rem 1.5rem;
  font-family: ${props => props.theme.fonts.accent};
  font-weight: 600;
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.text};
  border-bottom: 3px solid ${props => props.active ? props.theme.colors.primary : 'transparent'};
  margin-bottom: -1px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const ArticleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: ${props => props.theme.colors.background};
  border-bottom: 2px solid ${props => props.theme.colors.divider};
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${props => props.theme.colors.divider};
  
  &:hover {
    background-color: rgba(29, 110, 60, 0.05);
  }
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 1rem;
  font-family: ${props => props.theme.fonts.accent};
  font-weight: 600;
`;

const TableCell = styled.td`
  padding: 1rem;
  vertical-align: middle;
`;

const ArticleTitle = styled(Link)`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-family: ${props => props.theme.fonts.accent};
  font-weight: 600;
  background-color: ${props => {
    switch(props.status) {
      case 'draft': return '#f0f0f0';
      case 'review': return '#fff8e6';
      case 'approved': return '#e6f7ef';
      case 'published': return '#e6f0ff';
      default: return '#f0f0f0';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'draft': return '#666666';
      case 'review': return '#d4a017';
      case 'approved': return '#1d6e3c';
      case 'published': return '#2563eb';
      default: return '#666666';
    }
  }};
`;

const ActionButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  color: ${props => props.theme.colors.text};
  background-color: white;
  border: 1px solid ${props => props.theme.colors.divider};
  margin-right: 0.5rem;
  
  &:hover {
    background-color: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.primary};
  }
  
  &:after {
    display: none;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 0;
  color: ${props => props.theme.colors.text};
  opacity: 0.6;
`;

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('myArticles');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingComments, setPendingComments] = useState(0);
  const { currentUser } = useAuth();
  
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        
        // Try to fetch real articles first
        let articlesData;
        try {
          articlesData = await articleService.getArticles({ limit: 50 });
        } catch (error) {
          console.log('Using demo data');
          // Fall back to demo data
          const placeholderArticles = [];
          const statuses = ['draft', 'review', 'approved', 'published'];
          const sections = ['news', 'sports', 'features', 'opinion'];
          
          for (let i = 0; i < 15; i++) {
            const date = new Date();
            date.setDate(date.getDate() - i);
            
            placeholderArticles.push({
              id: `article-${i}`,
              title: `Article ${i + 1}: ${getRandomTitle(i % sections.length)}`,
              author: currentUser?.displayName || 'Current User',
              section: sections[i % sections.length],
              status: statuses[i % statuses.length],
              updated_at: date.toISOString(),
              created_at: date.toISOString()
            });
          }
          articlesData = placeholderArticles;
        }
        
        setArticles(articlesData);
        
        // Load pending comments count
        try {
          const pendingCommentsData = await commentService.getPendingComments();
          setPendingComments(pendingCommentsData.length);
        } catch (error) {
          console.error('Error loading pending comments:', error);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching articles:', error);
        setLoading(false);
      }
    };
    
    fetchArticles();
  }, [currentUser]);
  
  // Helper function to generate random article titles
  const getRandomTitle = (sectionIndex) => {
    const titles = [
      ['School Board Meeting', 'New Policy Announced', 'Student Achievement Award'],
      ['Game Recap', 'Athlete Profile', 'Season Preview'],
      ['Teacher Spotlight', 'Club Activities', 'Student Life'],
      ['Editorial on School Policy', 'Letter to Editor', 'Student Opinion Piece']
    ];
    
    const sectionTitles = titles[sectionIndex];
    return sectionTitles[Math.floor(Math.random() * sectionTitles.length)];
  };
  
  // Filter articles based on active tab
  const getFilteredArticles = () => {
    switch (activeTab) {
      case 'myArticles':
        return articles.filter(article => article.author === (currentUser?.displayName || 'Current User'));
      case 'needsReview':
        return articles.filter(article => article.status === 'review');
      case 'approved':
        return articles.filter(article => article.status === 'approved');
      case 'published':
        return articles.filter(article => article.status === 'published');
      default:
        return articles;
    }
  };
  
  const filteredArticles = getFilteredArticles();
  
  return (
    <DashboardContainer>
      <div className="container">
        <DashboardHeader>
          <DashboardTitle>Newsroom Dashboard</DashboardTitle>
          <NewArticleButton to="/admin/article/new">
            <FiPlus size={18} /> New Article
          </NewArticleButton>
        </DashboardHeader>
        
        <TabsContainer>
          <Tab 
            active={activeTab === 'myArticles'} 
            onClick={() => setActiveTab('myArticles')}
          >
            <FiList size={18} /> My Articles
          </Tab>
          <Tab 
            active={activeTab === 'needsReview'} 
            onClick={() => setActiveTab('needsReview')}
          >
            <FiEdit size={18} /> Needs Review
          </Tab>
          <Tab 
            active={activeTab === 'approved'} 
            onClick={() => setActiveTab('approved')}
          >
            <FiCalendar size={18} /> Approved
          </Tab>
          <Tab 
            active={activeTab === 'published'} 
            onClick={() => setActiveTab('published')}
          >
            <FiBarChart2 size={18} /> Published
          </Tab>
        </TabsContainer>
        
        {pendingComments > 0 && (
          <div style={{ 
            background: '#fff3cd', 
            border: '1px solid #ffeaa7', 
            borderRadius: '4px', 
            padding: '1rem', 
            marginBottom: '2rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>You have {pendingComments} comment{pendingComments !== 1 ? 's' : ''} waiting for approval.</span>
            <Link 
              to="/admin/comments" 
              style={{ 
                background: '#2E7D32', 
                color: 'white', 
                padding: '0.5rem 1rem', 
                borderRadius: '4px', 
                textDecoration: 'none' 
              }}
            >
              Review Comments
            </Link>
          </div>
        )}
        
        {filteredArticles.length > 0 ? (
          <ArticleTable>
            <TableHead>
              <tr>
                <TableHeader>Title</TableHeader>
                <TableHeader>Section</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Last Updated</TableHeader>
                <TableHeader>Actions</TableHeader>
              </tr>
            </TableHead>
            <tbody>
              {filteredArticles.map(article => (
                <TableRow key={article.id}>
                  <TableCell>
                    <ArticleTitle to={`/admin/article/${article.id}`}>
                      {article.title}
                    </ArticleTitle>
                  </TableCell>
                  <TableCell>
                    {article.section.charAt(0).toUpperCase() + article.section.slice(1)}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={article.status}>
                      {article.status.charAt(0).toUpperCase() + article.status.slice(1)}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    {new Date(article.updated_at || article.updatedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </TableCell>
                  <TableCell>
                    <ActionButton to={`/admin/article/${article.id}`}>
                      <FiEdit size={16} />
                    </ActionButton>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </ArticleTable>
        ) : (
          <EmptyState>
            {loading ? 'Loading articles...' : 'No articles found.'}
          </EmptyState>
        )}
      </div>
    </DashboardContainer>
  );
};

export default AdminDashboard;